import clsx from 'clsx';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

export function getVisiblePages(currentPageIndex: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }

  if (currentPageIndex <= 2) {
    return [0, 1, 2, 'ellipsis', totalPages - 1];
  }

  if (currentPageIndex >= totalPages - 3) {
    return [0, 'ellipsis', totalPages - 3, totalPages - 2, totalPages - 1];
  }

  return [0, 'ellipsis', currentPageIndex - 1, currentPageIndex, currentPageIndex + 1, 'ellipsis', totalPages - 1];
}

export interface PaginationProps {
  /** Current page number (1-based by default). */
  page: number;
  /** Whether `page` is 0-based or 1-based. Defaults to 1. */
  pageBase?: 0 | 1;
  totalPages: number;
  onPageChange: (page: number) => void;
  /** Inclusive start index for the summary (1-based item numbering). */
  rangeStart: number;
  /** Inclusive end index for the summary (1-based item numbering). */
  rangeEnd: number;
  /** Total item count for the summary. */
  totalItems: number;
  itemLabel?: string;
  className?: string;
  hideWhenSinglePage?: boolean;
  /** When false, only prev/next chevrons are shown. Defaults to true. */
  showPageNumbers?: boolean;
  /** Override next-button disabled state (e.g. cursor-based APIs). */
  hasNextPage?: boolean;
  /** Override prev-button disabled state. */
  hasPrevPage?: boolean;
}

export function Pagination({
  page,
  pageBase = 1,
  totalPages,
  onPageChange,
  rangeStart,
  rangeEnd,
  totalItems,
  itemLabel = 'results',
  className,
  hideWhenSinglePage = false,
  showPageNumbers = true,
  hasNextPage,
  hasPrevPage,
}: PaginationProps) {
  if (hideWhenSinglePage && totalPages <= 1) {
    return null;
  }

  const pageIndex = pageBase === 0 ? page : page - 1;
  const isPrevDisabled = hasPrevPage !== undefined ? !hasPrevPage : pageIndex <= 0;
  const isNextDisabled =
    hasNextPage !== undefined ? !hasNextPage : pageIndex >= totalPages - 1;

  const goToPage = (nextPageIndex: number) => {
    onPageChange(pageBase === 0 ? nextPageIndex : nextPageIndex + 1);
  };

  return (
    <div
      className={clsx(
        'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3',
        className
      )}
    >
      <div className="text-sm text-gray-700">
        Showing{' '}
        <span className="font-bold">
          {rangeStart}-{rangeEnd}
        </span>{' '}
        of <span className="font-bold">{totalItems}</span> {itemLabel}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => goToPage(pageIndex - 1)}
          disabled={isPrevDisabled}
          className="p-1 text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>

        {showPageNumbers &&
          getVisiblePages(pageIndex, totalPages).map((visiblePage, index) =>
            visiblePage === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-1 text-sm text-gray-500">
                ...
              </span>
            ) : (
              <button
                key={visiblePage}
                type="button"
                onClick={() => goToPage(visiblePage)}
                className={clsx(
                  'min-w-[2rem] px-2 py-1 text-sm',
                  pageIndex === visiblePage
                    ? 'rounded-md bg-gray-100 font-bold text-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                )}
              >
                {visiblePage + 1}
              </button>
            )
          )}

        <button
          type="button"
          onClick={() => goToPage(pageIndex + 1)}
          disabled={isNextDisabled}
          className="p-1 text-gray-900 disabled:text-gray-300 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
